/**
 * Scheduled agent tasks — one-time, recurring (RFC-5545 rrule) or event-triggered
 * jobs whose action is a reminder, an agent prompt, or a skill run.
 * Backend: `/v1/tasks` (src/api/v1/routers/tasks.py).
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { requireData } from '../errors.js'

export type TaskResponse = components['schemas']['TaskResponse']
export type CreateTaskRequest = components['schemas']['CreateTaskRequest']
export type UpdateTaskRequest = components['schemas']['UpdateTaskRequest']

/** List the caller's scheduled tasks (newest first). */
export async function listTasks(arbi: ArbiClient): Promise<TaskResponse[]> {
  return requireData(await arbi.fetch.GET('/v1/tasks'), 'Failed to list tasks')
}

/** Create a scheduled task. `name` is required; supply trigger + action fields. */
export async function createTask(arbi: ArbiClient, body: CreateTaskRequest): Promise<TaskResponse> {
  return requireData(await arbi.fetch.POST('/v1/tasks', { body }), 'Failed to create task')
}

export async function getTask(arbi: ArbiClient, taskId: string): Promise<TaskResponse> {
  return requireData(
    await arbi.fetch.GET('/v1/tasks/{task_id}', { params: { path: { task_id: taskId } } }),
    'Failed to get task'
  )
}

/** Update a task — including `status` to pause/resume (`active` | `paused`). */
export async function updateTask(
  arbi: ArbiClient,
  taskId: string,
  body: UpdateTaskRequest
): Promise<TaskResponse> {
  return requireData(
    await arbi.fetch.PATCH('/v1/tasks/{task_id}', {
      params: { path: { task_id: taskId } },
      body,
    }),
    'Failed to update task'
  )
}

export async function deleteTask(arbi: ArbiClient, taskId: string): Promise<void> {
  const { error } = await arbi.fetch.DELETE('/v1/tasks/{task_id}', {
    params: { path: { task_id: taskId } },
  })
  if (error) throw new Error('Failed to delete task')
}

/** Fire a task immediately without advancing its schedule. */
export async function runTask(arbi: ArbiClient, taskId: string): Promise<TaskResponse> {
  return requireData(
    await arbi.fetch.POST('/v1/tasks/{task_id}/run', { params: { path: { task_id: taskId } } }),
    'Failed to run task'
  )
}
